import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Plus,
  Search,
  GraduationCap,
  Users,
  Calendar,
  Euro,
  Clock,
  Check,
  X,
  MoreVertical,
  FileText,
  TrendingUp,
  AlertTriangle,
  BookOpen
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CATEGORIES = {
  'securite': 'Securite',
  'technique': 'Technique',
  'management': 'Management',
  'reglementaire': 'Reglementaire',
  'informatique': 'Informatique',
  'langues': 'Langues',
  'soft_skills': 'Soft Skills',
  'autre': 'Autre'
};

const STATUTS = {
  'en_cours_validation': { label: 'En cours de validation', color: 'bg-purple-100 text-purple-800' },
  'planifiee': { label: 'Planifiée', color: 'bg-blue-100 text-blue-800' },
  'en_cours': { label: 'En cours', color: 'bg-yellow-100 text-yellow-800' },
  'terminee': { label: 'Terminée', color: 'bg-green-100 text-green-800' },
  'annulee': { label: 'Annulée', color: 'bg-red-100 text-red-800' },
  'reportee': { label: 'Reportée', color: 'bg-orange-100 text-orange-800' }
};

const STATUTS_DEMANDE = {
  'en_attente': { label: 'En attente', color: 'bg-yellow-100 text-yellow-800' },
  'approuvee': { label: 'Approuvée', color: 'bg-green-100 text-green-800' },
  'refusee': { label: 'Refusée', color: 'bg-red-100 text-red-800' }
};

const Formations = () => {
  const [formations, setFormations] = useState([]);
  const [demandes, setDemandes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [budget, setBudget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatut, setFilterStatut] = useState('all');
  const [filterCategorie, setFilterCategorie] = useState('all');
  const [isFormationDialogOpen, setIsFormationDialogOpen] = useState(false);
  const [isDemandeDialogOpen, setIsDemandeDialogOpen] = useState(false);
  const [editingFormation, setEditingFormation] = useState(null);
  const [activeTab, setActiveTab] = useState('formations');
  const [participantsDialogOpen, setParticipantsDialogOpen] = useState(false);
  const [selectedFormationForParticipants, setSelectedFormationForParticipants] = useState(null);

  const userRole = JSON.parse(atob(localStorage.getItem('token')?.split('.')[1] || 'e30=')).role || '';
  const canManageDemandes = ['admin', 'rh', 'super_admin'].includes(userRole);

  const [formData, setFormData] = useState({
    titre: '', description: '', categorie: 'autre', type_formation: 'interne',
    organisme: '', formateur: '', lieu: '', date_debut: '', date_fin: '',
    duree_heures: 0, cout_total: 0, cout_par_participant: 0, budget_source: 'plan_formation',
    objectifs: '', competences_visees: [], prerequis: '', certification: false,
    nom_certification: '', obligatoire: false, statut: 'planifiee', participants: []
  });

  const [demandeData, setDemandeData] = useState({
    titre: '', description: '', organisme_souhaite: '', cout_estime: 0,
    duree_estimee: '', urgence: 'normale', justification: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const year = new Date().getFullYear();
      const [formationsRes, employeesRes, demandesRes, budgetRes] = await Promise.all([
        axios.get(`${API_URL}/formations`, { headers }),
        axios.get(`${API_URL}/employees`, { headers }),
        axios.get(`${API_URL}/formations/demandes`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/formations/budget/${year}`, { headers }).catch(() => ({ data: null }))
      ]);
      setFormations(formationsRes.data);
      setEmployees(employeesRes.data);
      setDemandes(demandesRes.data);
      setBudget(budgetRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des donnees');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFormation = async () => {
    if (!formData.titre || !formData.date_debut) {
      toast.error('Le titre et la date de debut sont obligatoires');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      if (editingFormation) {
        await axios.put(`${API_URL}/formations/${editingFormation.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Formation modifiée');
      } else {
        await axios.post(`${API_URL}/formations`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Formation creee');
      }
      setIsFormationDialogOpen(false);
      setEditingFormation(null);
      resetFormData();
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleCreateDemande = async () => {
    if (!demandeData.titre) {
      toast.error('Le titre est obligatoire');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/formations/demandes`, demandeData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Demande envoyee');
      setIsDemandeDialogOpen(false);
      setDemandeData({ titre: '', description: '', organisme_souhaite: '', cout_estime: 0, duree_estimee: '', urgence: 'normale', justification: '' });
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'envoi de la demande');
    }
  };

  const handleValidateDemande = async (demandeId, statut) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/formations/demandes/${demandeId}/validate`, { statut }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Demande ${statut === 'approuvee' ? 'approuvee' : 'refusee'}`);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la validation');
    }
  };

  const handleDeleteFormation = async (id) => {
    if (!window.confirm('Supprimer cette formation ?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/formations/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Formation supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const openEditDialog = (formation) => {
    setEditingFormation(formation);
    setFormData({
      titre: formation.titre || '', description: formation.description || '',
      categorie: formation.categorie || 'autre', type_formation: formation.type_formation || 'interne',
      organisme: formation.organisme || '', formateur: formation.formateur || '',
      lieu: formation.lieu || '', date_debut: formation.date_debut || '', date_fin: formation.date_fin || '',
      duree_heures: formation.duree_heures || 0, cout_total: formation.cout_total || 0,
      cout_par_participant: formation.cout_par_participant || 0,
      budget_source: formation.budget_source || 'plan_formation',
      objectifs: formation.objectifs || '', competences_visees: formation.competences_visees || [],
      prerequis: formation.prerequis || '', certification: formation.certification || false,
      nom_certification: formation.nom_certification || '', obligatoire: formation.obligatoire || false,
      statut: formation.statut || 'planifiee', participants: formation.participants || []
    });
    setIsFormationDialogOpen(true);
  };

  const resetFormData = () => {
    setFormData({
      titre: '', description: '', categorie: 'autre', type_formation: 'interne',
      organisme: '', formateur: '', lieu: '', date_debut: '', date_fin: '',
      duree_heures: 0, cout_total: 0, cout_par_participant: 0, budget_source: 'plan_formation',
      objectifs: '', competences_visees: [], prerequis: '', certification: false,
      nom_certification: '', obligatoire: false, statut: 'planifiee', participants: []
    });
  };

  const handleUpdateParticipants = async (formationId, participantIds) => {
    try {
      const token = localStorage.getItem('token');
      const formation = formations.find(f => f.id === formationId);
      if (!formation) return;
      await axios.put(`${API_URL}/formations/${formationId}`, {
        ...formation,
        participants: participantIds
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Participants mis a jour');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise a jour des participants');
    }
  };

  const today = new Date().toISOString().split('T')[0];

  const activeFormations = formations.filter(f => {
    const isArchive = f.date_fin && f.date_fin < today && f.statut === 'terminee';
    return !isArchive;
  });

  const archivedFormations = formations.filter(f => {
    return (f.date_fin && f.date_fin < today) || f.statut === 'terminee' || f.statut === 'annulee';
  });

  const filteredFormations = activeFormations.filter(f => {
    const matchSearch = !searchTerm || f.titre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      f.organisme?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatut = filterStatut === 'all' || f.statut === filterStatut;
    const matchCat = filterCategorie === 'all' || f.categorie === filterCategorie;
    return matchSearch && matchStatut && matchCat;
  });

  const filteredArchives = archivedFormations.filter(f => {
    const matchSearch = !searchTerm || f.titre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      f.organisme?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchSearch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0F5156]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Formation Continue</h1>
          <p className="text-slate-600 dark:text-slate-400">Gestion du plan de formation et des competences</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setIsDemandeDialogOpen(true)}>
            <FileText className="w-4 h-4 mr-2" />
            Demande
          </Button>
          <Button className="bg-[#0F5156] hover:bg-[#0a3a3e]" onClick={() => { resetFormData(); setEditingFormation(null); setIsFormationDialogOpen(true); }}>
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle formation
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      {budget && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <GraduationCap className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Formations</p>
                  <p className="text-xl font-bold">{budget.total_formations}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <Users className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Participants</p>
                  <p className="text-xl font-bold">{budget.nb_participants_uniques}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Heures totales</p>
                  <p className="text-xl font-bold">{budget.total_heures}h</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Euro className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Budget total</p>
                  <p className="text-xl font-bold">{budget.total_cout?.toLocaleString('fr-FR')} &euro;</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="formations">Plan de formation</TabsTrigger>
          <TabsTrigger value="archives">Archives</TabsTrigger>
          <TabsTrigger value="demandes">Demandes ({demandes.length})</TabsTrigger>
          <TabsTrigger value="budget">Budget</TabsTrigger>
        </TabsList>

        <TabsContent value="formations" className="space-y-4">
          {/* Filters */}
          <div className="flex gap-4 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Rechercher..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
            </div>
            <Select value={filterStatut} onValueChange={setFilterStatut}>
              <SelectTrigger className="w-[180px]"><SelectValue placeholder="Statut" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                {Object.entries(STATUTS).map(([key, val]) => (
                  <SelectItem key={key} value={key}>{val.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterCategorie} onValueChange={setFilterCategorie}>
              <SelectTrigger className="w-[180px]"><SelectValue placeholder="Categorie" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les categories</SelectItem>
                {Object.entries(CATEGORIES).map(([key, val]) => (
                  <SelectItem key={key} value={key}>{val}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Table */}
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Formation</TableHead>
                    <TableHead>Categorie</TableHead>
                    <TableHead>Dates</TableHead>
                    <TableHead>Duree</TableHead>
                    <TableHead>Participants</TableHead>
                    <TableHead>Cout</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredFormations.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                        Aucune formation trouvee
                      </TableCell>
                    </TableRow>
                  ) : filteredFormations.map(f => (
                    <TableRow key={f.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{f.titre}</p>
                          <p className="text-sm text-slate-500">{f.organisme || 'Interne'}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{CATEGORIES[f.categorie] || f.categorie}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {f.date_debut && <span>{new Date(f.date_debut).toLocaleDateString('fr-FR')}</span>}
                          {f.date_fin && <span className="text-slate-400"> - {new Date(f.date_fin).toLocaleDateString('fr-FR')}</span>}
                        </div>
                      </TableCell>
                      <TableCell>{f.duree_heures}h</TableCell>
                      <TableCell>{f.participants?.length || 0}</TableCell>
                      <TableCell>{f.cout_total ? `${f.cout_total.toLocaleString('fr-FR')} €` : '-'}</TableCell>
                      <TableCell>
                        <Badge className={STATUTS[f.statut]?.color || 'bg-gray-100'}>
                          {STATUTS[f.statut]?.label || f.statut}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm"><MoreVertical className="w-4 h-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent>
                            <DropdownMenuItem onClick={() => openEditDialog(f)}>Modifier</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => { setSelectedFormationForParticipants(f); setParticipantsDialogOpen(true); }}>Gérer les participants</DropdownMenuItem>
                            <DropdownMenuItem className="text-red-600" onClick={() => handleDeleteFormation(f.id)}>Supprimer</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="archives" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Formations archivées</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Formation</TableHead>
                    <TableHead>Categorie</TableHead>
                    <TableHead>Dates</TableHead>
                    <TableHead>Duree</TableHead>
                    <TableHead>Participants</TableHead>
                    <TableHead>Cout</TableHead>
                    <TableHead>Statut</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredArchives.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                        Aucune formation archivée
                      </TableCell>
                    </TableRow>
                  ) : filteredArchives.map(f => (
                    <TableRow key={f.id} className="opacity-75">
                      <TableCell>
                        <div>
                          <p className="font-medium">{f.titre}</p>
                          <p className="text-sm text-slate-500">{f.organisme || 'Interne'}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{CATEGORIES[f.categorie] || f.categorie}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {f.date_debut && <span>{new Date(f.date_debut).toLocaleDateString('fr-FR')}</span>}
                          {f.date_fin && <span className="text-slate-400"> - {new Date(f.date_fin).toLocaleDateString('fr-FR')}</span>}
                        </div>
                      </TableCell>
                      <TableCell>{f.duree_heures}h</TableCell>
                      <TableCell>{f.participants?.length || 0}</TableCell>
                      <TableCell>{f.cout_total ? `${f.cout_total.toLocaleString('fr-FR')} €` : '-'}</TableCell>
                      <TableCell>
                        <Badge className={STATUTS[f.statut]?.color || 'bg-gray-100'}>
                          {STATUTS[f.statut]?.label || f.statut}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="demandes" className="space-y-4">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Demandeur</TableHead>
                    <TableHead>Formation souhaitee</TableHead>
                    <TableHead>Organisme</TableHead>
                    <TableHead>Cout estime</TableHead>
                    <TableHead>Urgence</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {demandes.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                        Aucune demande de formation
                      </TableCell>
                    </TableRow>
                  ) : demandes.map(d => (
                    <TableRow key={d.id}>
                      <TableCell>
                        <span className="font-medium">{d.employee_prenom} {d.employee_nom}</span>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{d.titre}</p>
                          {d.description && <p className="text-sm text-slate-500 truncate max-w-xs">{d.description}</p>}
                        </div>
                      </TableCell>
                      <TableCell>{d.organisme_souhaite || '-'}</TableCell>
                      <TableCell>{d.cout_estime ? `${d.cout_estime.toLocaleString('fr-FR')} €` : '-'}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={d.urgence === 'haute' ? 'border-red-300 text-red-700' : ''}>
                          {d.urgence}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={STATUTS_DEMANDE[d.statut]?.color || 'bg-gray-100'}>
                          {STATUTS_DEMANDE[d.statut]?.label || d.statut}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {canManageDemandes ? (
                          <div className="flex gap-1 items-center">
                            {d.statut === 'en_attente' && (
                              <>
                                <Button size="sm" variant="ghost" className="text-green-600" onClick={() => handleValidateDemande(d.id, 'approuvee')}>
                                  <Check className="w-4 h-4" />
                                </Button>
                                <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleValidateDemande(d.id, 'refusee')}>
                                  <X className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                            {d.statut !== 'en_attente' && (
                              <Select value={d.statut} onValueChange={(v) => handleValidateDemande(d.id, v)}>
                                <SelectTrigger className="w-[140px] h-8 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {Object.entries(STATUTS_DEMANDE).map(([k, v]) => (
                                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-slate-500">-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="budget" className="space-y-4">
          {budget ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Repartition par categorie</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(budget.par_categorie || {}).map(([cat, data]) => (
                      <div key={cat} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                        <div>
                          <p className="font-medium">{CATEGORIES[cat] || cat}</p>
                          <p className="text-sm text-slate-500">{data.count} formation(s) - {data.heures}h</p>
                        </div>
                        <span className="font-semibold">{data.cout?.toLocaleString('fr-FR')} &euro;</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Statistiques</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <span className="text-slate-600">Cout moyen par formation</span>
                      <span className="font-semibold">{budget.cout_moyen_par_formation?.toLocaleString('fr-FR')} &euro;</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <span className="text-slate-600">Duree moyenne</span>
                      <span className="font-semibold">{budget.heures_moyen_par_formation}h</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <span className="text-slate-600">Repartition par statut</span>
                      <div className="flex gap-2">
                        {Object.entries(budget.par_statut || {}).map(([s, count]) => (
                          <Badge key={s} className={STATUTS[s]?.color || 'bg-gray-100'}>
                            {STATUTS[s]?.label || s}: {count}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card>
              <CardContent className="p-8 text-center text-slate-500">
                Aucune donnee budgetaire disponible
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Formation Dialog */}
      <Dialog open={isFormationDialogOpen} onOpenChange={setIsFormationDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingFormation ? 'Modifier la formation' : 'Nouvelle formation'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Titre *</Label>
                <Input value={formData.titre} onChange={(e) => setFormData({...formData, titre: e.target.value})} />
              </div>
              <div>
                <Label>Categorie</Label>
                <Select value={formData.categorie} onValueChange={(v) => setFormData({...formData, categorie: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(CATEGORIES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Type</Label>
                <Select value={formData.type_formation} onValueChange={(v) => setFormData({...formData, type_formation: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="interne">Interne</SelectItem>
                    <SelectItem value="externe">Externe</SelectItem>
                    <SelectItem value="e_learning">E-learning</SelectItem>
                    <SelectItem value="mixte">Mixte</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Organisme</Label>
                <Input value={formData.organisme} onChange={(e) => setFormData({...formData, organisme: e.target.value})} />
              </div>
              <div>
                <Label>Formateur</Label>
                <Input value={formData.formateur} onChange={(e) => setFormData({...formData, formateur: e.target.value})} />
              </div>
              <div>
                <Label>Lieu</Label>
                <Input value={formData.lieu} onChange={(e) => setFormData({...formData, lieu: e.target.value})} />
              </div>
              <div>
                <Label>Statut</Label>
                <Select value={formData.statut} onValueChange={(v) => setFormData({...formData, statut: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUTS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date debut *</Label>
                <Input type="date" value={formData.date_debut} onChange={(e) => setFormData({...formData, date_debut: e.target.value})} />
              </div>
              <div>
                <Label>Date fin</Label>
                <Input type="date" value={formData.date_fin} onChange={(e) => setFormData({...formData, date_fin: e.target.value})} />
              </div>
              <div>
                <Label>Duree (heures)</Label>
                <Input type="number" value={formData.duree_heures} onChange={(e) => setFormData({...formData, duree_heures: parseFloat(e.target.value) || 0})} />
              </div>
              <div>
                <Label>Cout total (euros)</Label>
                <Input type="number" value={formData.cout_total} onChange={(e) => setFormData({...formData, cout_total: parseFloat(e.target.value) || 0})} />
              </div>
              <div className="col-span-2">
                <Label>Objectifs</Label>
                <Textarea value={formData.objectifs} onChange={(e) => setFormData({...formData, objectifs: e.target.value})} rows={3} />
              </div>
              <div className="col-span-2">
                <Label>Description</Label>
                <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} rows={3} />
              </div>
              <div className="flex items-center gap-4 col-span-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={formData.obligatoire} onChange={(e) => setFormData({...formData, obligatoire: e.target.checked})} className="rounded" />
                  <span className="text-sm">Formation obligatoire</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={formData.certification} onChange={(e) => setFormData({...formData, certification: e.target.checked})} className="rounded" />
                  <span className="text-sm">Certifiante</span>
                </label>
              </div>
              {formData.certification && (
                <div className="col-span-2">
                  <Label>Nom de la certification</Label>
                  <Input value={formData.nom_certification} onChange={(e) => setFormData({...formData, nom_certification: e.target.value})} />
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setIsFormationDialogOpen(false)}>Annuler</Button>
              <Button className="bg-[#0F5156] hover:bg-[#0a3a3e]" onClick={handleCreateFormation}>
                {editingFormation ? 'Enregistrer' : 'Creer'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Participants Dialog */}
      <Dialog open={participantsDialogOpen} onOpenChange={setParticipantsDialogOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Gérer les participants - {selectedFormationForParticipants?.titre}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-500">Sélectionnez les collaborateurs à inscrire à cette formation :</p>
            <div className="space-y-2 max-h-[50vh] overflow-y-auto">
              {employees.map(emp => {
                const isSelected = (selectedFormationForParticipants?.participants || []).includes(emp.id);
                return (
                  <label key={emp.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => {
                        const current = selectedFormationForParticipants?.participants || [];
                        const updated = e.target.checked
                          ? [...current, emp.id]
                          : current.filter(id => id !== emp.id);
                        setSelectedFormationForParticipants({...selectedFormationForParticipants, participants: updated});
                      }}
                      className="rounded"
                    />
                    <div>
                      <span className="font-medium">{emp.prenom} {emp.nom}</span>
                      {emp.poste && <span className="text-sm text-slate-500 ml-2">- {emp.poste}</span>}
                    </div>
                  </label>
                );
              })}
            </div>
            <div className="flex justify-between items-center pt-4">
              <span className="text-sm text-slate-500">{(selectedFormationForParticipants?.participants || []).length} participant(s) selectionne(s)</span>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setParticipantsDialogOpen(false)}>Annuler</Button>
                <Button className="bg-[#0F5156] hover:bg-[#0a3a3e]" onClick={() => {
                  if (selectedFormationForParticipants) {
                    handleUpdateParticipants(selectedFormationForParticipants.id, selectedFormationForParticipants.participants || []);
                    setParticipantsDialogOpen(false);
                  }
                }}>Enregistrer</Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Demande Dialog */}
      <Dialog open={isDemandeDialogOpen} onOpenChange={setIsDemandeDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Demande de formation</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Titre de la formation souhaitée *</Label>
              <Input value={demandeData.titre} onChange={(e) => setDemandeData({...demandeData, titre: e.target.value})} />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={demandeData.description} onChange={(e) => setDemandeData({...demandeData, description: e.target.value})} rows={3} />
            </div>
            <div>
              <Label>Organisme souhaite</Label>
              <Input value={demandeData.organisme_souhaite} onChange={(e) => setDemandeData({...demandeData, organisme_souhaite: e.target.value})} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Cout estime (euros)</Label>
                <Input type="number" value={demandeData.cout_estime} onChange={(e) => setDemandeData({...demandeData, cout_estime: parseFloat(e.target.value) || 0})} />
              </div>
              <div>
                <Label>Urgence</Label>
                <Select value={demandeData.urgence} onValueChange={(v) => setDemandeData({...demandeData, urgence: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="basse">Basse</SelectItem>
                    <SelectItem value="normale">Normale</SelectItem>
                    <SelectItem value="haute">Haute</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Justification</Label>
              <Textarea value={demandeData.justification} onChange={(e) => setDemandeData({...demandeData, justification: e.target.value})} rows={3} placeholder="Pourquoi cette formation est-elle necessaire ?" />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setIsDemandeDialogOpen(false)}>Annuler</Button>
              <Button className="bg-[#0F5156] hover:bg-[#0a3a3e]" onClick={handleCreateDemande}>Envoyer la demande</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Formations;
